import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { MARKETING_CHANNEL_ID } from '../theme/theme.constants';

const {
  akc,
  figo,
  ppi,
  ppiG,
  ppu,
  ppuPetCare,
  spotCan,
} = MARKETING_CHANNEL_ID;

const MarketingChannelUS = [akc, figo, ppi, ppiG];
const MarketingChannelWithLiveVet = [akc, figo, ppi, ppiG];
const MarketingChannelWithGetAQuote = [akc, figo, ppi, ppiG];

/**
 * Returns the user brand companies from personal information endpoint.
 * @returns Banbrand list array or brand list joined.
 */
export const useUserBrands = () => {
  const store = useSelector(({ personalInformation }) => ({
    personalInformation,
  }), shallowEqual);

  const { personalInformation: { userInfo } } = store;

  const userBrandCompanies = useMemo(() => {
    if (!userInfo || !userInfo.Companies) {
      return [];
    }

    const companies = userInfo.Companies
      .map((company) => company.OriginId) || [];

    return companies;
  }, [userInfo]);

  const userBrandCompaniesJoined = useMemo(() => userBrandCompanies
    .join(','), [userBrandCompanies]);

  const hasPolicyPPU = useMemo(() => {
    const ppuCompany = userInfo.Companies?.find(({ Id }) => Id === ppu);

    return Boolean(ppuCompany);
  }, [userInfo]);

  const hasPolicySpotCan = useMemo(() => {
    const ppuCompany = userInfo.Companies?.find(({ Id }) => Id === spotCan);

    return Boolean(ppuCompany);
  }, [userInfo]);

  const hasPolicyPPUPetCare = useMemo(() => {
    const ppuCompany = userInfo.Companies?.find(({ Id }) => Id === ppuPetCare);

    return Boolean(ppuCompany);
  }, [userInfo]);

  const hasCanadianPolicies = useMemo(() => hasPolicyPPU
    || hasPolicySpotCan || hasPolicyPPUPetCare, [
    hasPolicyPPU, hasPolicySpotCan, hasPolicyPPUPetCare]);

  const hasUSPolicies = useMemo(() => {
    const show = userInfo
      .Companies?.some(({ Id }) => MarketingChannelUS.includes(Id));

    return Boolean(show);
  }, [userInfo]);

  const showLiveVetSection = useMemo(() => {
    const show = userInfo
      .Companies?.some(({ Id }) => MarketingChannelWithLiveVet.includes(Id));

    return Boolean(show);
  }, [userInfo]);

  const showAddPet = useMemo(() => {
    const ppuCompany = userInfo.Companies
      ?.find(({ Id }) => MarketingChannelWithGetAQuote.includes(Id));

    return Boolean(ppuCompany);
  }, [userInfo]);

  const showAddMicrochip = useMemo(() => {
    const MarketingChannelWithMicrochip = [akc, figo, ppi, ppiG];
    const ppuCompany = userInfo.Companies
      ?.find(({ Id }) => MarketingChannelWithMicrochip.includes(Id));

    return Boolean(ppuCompany);
  }, [userInfo]);

  const hideReimbursementScreen = useMemo(() => {
    const ppuCompany = userInfo.Companies?.find(({ Id }) => Id === ppu
      || Id === ppuPetCare
      || Id === MARKETING_CHANNEL_ID.spotCan);

    return Boolean(ppuCompany);
  }, [userInfo]);

  return {
    hasCanadianPolicies,
    hasPolicyPPU,
    hasPolicyPPUPetCare,
    hasPolicySpotCan,
    hasUSPolicies,
    hideReimbursementScreen,
    showAddMicrochip,
    showAddPet,
    showLiveVetSection,
    userBrandCompanies,
    userBrandCompaniesJoined,
  };
};
